import { Button, Col, Form, Row, Space, Table } from "antd";
import { CloseOutlined, ReloadOutlined, TeamOutlined } from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { RootState } from "../../core/store";
import { allPermissions } from "../../shared/constants/constants";
import { API_MISSING_STAFF_FIELDS, ROUTE_DASHBOARD, ROUTE_MANAGE_STAFF, ROUTE_STAFF_DASHBOARD, ROUTE_STAFF_PROFILE } from "../../shared/routes/private.routes";
import { accessAllowed } from "../../shared/utils/utils";
import DashboardLoadError from "./components/dashboard-load-error.component";
import { missingStaffFields, useMissingFieldsResource } from "./missing-fields";

type MissingStaff = { staffId: string; employeeId: string; staffName: string; email: string | null; role: string | null; lineManagerName: string | null; departmentName: string; designation: string; status: string | null; contractStartDate: string | null; contractEndDate: string | null; probationEndDate: string | null; nextPerformanceReviewDate: string | null; nextAppraisalSalaryRevisionDate: string | null };
type MissingStaffResponse = { items: MissingStaff[]; total: number };

export default function MissingStaffFieldsPage() {
  const { field = "" } = useParams();
  const config = Object.prototype.hasOwnProperty.call(missingStaffFields, field) ? missingStaffFields[field] : undefined;
  const [params, setParams] = useSearchParams();
  const [filterVisible, setFilterVisible] = useState(false);
  const [form] = Form.useForm();
  useEffect(() => {
    form.setFieldsValue({ search: params.get("search") || "", status: params.get("status") || undefined });
    if (params.has("departmentId")) {
      const next = new URLSearchParams(params);
      next.delete("departmentId");
      next.delete("page");
      setParams(next, { replace: true });
    }
  }, [params, setParams, form]);
  const location = useLocation();
  const navigate = useNavigate();
  const permissions = useSelector((state: RootState) => state.auth.myPermissions);
  const canView = accessAllowed(permissions || [], allPermissions.VIEW_STAFF);
  const canEdit = accessAllowed(permissions || [], allPermissions.UPDATE_STAFF);
  const requestedPage = Number(params.get("page") || 1);
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const query = new URLSearchParams({ field, page: String(page), limit: "10" });
  for (const key of ["search", "status"]) {
    if (params.get(key)) query.set(key, params.get(key)!);
  }
  const { data, loading, error, reload } = useMissingFieldsResource<MissingStaffResponse>(
    `${API_MISSING_STAFF_FIELDS}/staff?${query}`, canView && !!config,
  );
  useEffect(() => {
    if (loading || error || !data) return;
    const lastPage = Math.max(1, Math.ceil(data.total / 10));
    if (page > lastPage) {
      const next = new URLSearchParams(params);
      next.set("page", String(lastPage));
      setParams(next, { replace: true });
    }
  }, [data, loading, error, page, params, setParams]);
  const setFilter = (key: string, value?: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    next.delete("page");
    setParams(next, { replace: true });
  };
   const openStaffProfile = (entity: any) => {
      if (!entity?.staffId) return;
      navigate(`${ROUTE_MANAGE_STAFF}/${entity.staffId}`, {
        state: {staff : entity, missingField: field,returnTo: location.pathname + location.search},
    
      });
    };
  if (!canView) return <div role="alert">You do not have permission to view staff information.</div>;
  if (!config) return <div role="alert">Unknown missing field. <Link to={ROUTE_STAFF_DASHBOARD}>Back to Dashboard</Link></div>;

  return <div className="app-page">
    <div className="app-page-header">
      <Row align="middle" justify="space-between" wrap gutter={[16, 16]}>
        <Col flex="1" style={{ minWidth: 0 }}>
          <Link to={ROUTE_STAFF_DASHBOARD}>Back to Dashboard</Link>
          <div className="app-page-title-wrap">
            <div className="app-page-icon"><TeamOutlined /></div>
            <h1 className="app-page-title">Missing Information &ndash; {config.label}</h1>
          </div>
          <p className="app-page-subtitle">Only active and notice-period staff whose {config.label.toLowerCase()} is missing.</p>
        </Col>
        <Col>
          <Space size="middle" className="app-page-actions">
            <Button icon={<ReloadOutlined />} onClick={reload} disabled={loading}>Refresh</Button>
            <Button shape="circle" type="primary" icon={<BiFilterAlt />} onClick={() => setFilterVisible(!filterVisible)}
              title={filterVisible ? "Hide filters" : "Show filters"} aria-label={filterVisible ? "Hide filters" : "Show filters"} />
          </Space>
        </Col>
      </Row>
    </div>
    {filterVisible && <div className="app-filter-bar">
      <div className="app-filter-header">
        <div className="app-filter-title"><BiFilterAlt style={{ fontSize: 14 }} /> Filters</div>
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setFilterVisible(false)} title="Close filters" aria-label="Close filters" />
      </div>
      <div className="table-filter-form">
        <GenericFormComponent
          form={form} name="missing-staff-filters" enableEnterSubmit
          initialValues={{ search: params.get("search") || "", status: params.get("status") || undefined }}
          onFinishFailed={null}
          formElements={[
            { name: "search", label: "", placeholder: "Name Search", type: "Input", colSpanResponsive: { xs: 24, sm: 12, md: 8, lg: 6 } },
            {
              name: "status", label: "", placeholder: "Staff Status", type: "Select", allowClear: true,
              colSpanResponsive: { xs: 24, sm: 12, md: 8, lg: 6 },
              options: [{ label: "ACTIVE", value: "ACTIVE" }, { label: "NOTICE PERIOD", value: "NOTICE PERIOD" }]
            },
          ]}
          extraButtons={[
            { htmlType: "button", type: "clearForm", submitButtonText: "Clear", colSpanResponsive: { xs: 12, sm: 6, md: 4, lg: 3 }, onClick: () => { form.resetFields(); setParams({}, { replace: true }); } },
            { htmlType: "submit", submitButtonText: "Apply", submitButtonType: "primary", colSpanResponsive: { xs: 12, sm: 6, md: 4, lg: 3 } },
          ]}
          onFinish={(values: { search?: string; status?: string }) => {
            const next = new URLSearchParams();
            if (values.search?.trim()) next.set("search", values.search.trim());
            if (values.status) next.set("status", values.status);
            setParams(next, { replace: true });
          }}
        />
      </div>
    </div>}
    <ActiveFilterChipsBar show={!filterVisible}
      entries={[{ key: "search", fieldLabel: "Search", valueText: params.get("search") || "" }, { key: "status", fieldLabel: "Staff Status", valueText: params.get("status") || "" }].filter((entry) => !!entry.valueText)}
      onRemove={(key) => setFilter(key)} onClearAll={() => { form.resetFields(); setParams({}, { replace: true }); }} />
    <div className="app-table-card milestones-table-card">
      {error ? <DashboardLoadError message="Unable to load staff with missing information." onRetry={reload} /> :
        <Table<MissingStaff> rowKey="staffId" loading={loading} dataSource={loading ? [] : data?.items || []}  scroll={{ x: "max-content" }}
          tableLayout="fixed"
          locale={{ emptyText: loading ? "Loading staff…" : "No staff are missing this field for the selected filters." }}
          pagination={{
            current: page, pageSize: 10, total: data?.total || 0, showSizeChanger: false, onChange: (nextPage) => {
              const next = new URLSearchParams(params); next.set("page", String(nextPage)); setParams(next);
            }
          }}
          columns={[
            { title: "Staff ID", dataIndex: "employeeId" },
            { title: "Staff Name", dataIndex: "staffName" },
            { title: "Email", dataIndex: "email", render: (value: string | null) => value?.trim() || "" },
            { title: "Role", dataIndex: "role", render: (value: string | null) => value?.trim() || "" },
            { title: "Line Manager", dataIndex: "lineManagerName", render: (value: string | null) => value?.trim() || "" },
            {
              title: "Contract Start Date",
              dataIndex: "contractStartDate",
              width: '150px',
              render: (value: string | null) => value ? dayjs(value).format("DD-MMM-YYYY") : "",
            },

            {
              title: "Contract End Date",
              dataIndex: "contractEndDate",
              width: '150px',
              render: (value: string | null) => value ? dayjs(value).format("DD-MMM-YYYY") : "",
            },

            {
              title: "Probation End Date",
              dataIndex: "probationEndDate",
              width: '250px',
              render: (value: string | null) => value ? dayjs(value).format("DD-MMM-YYYY") : "",
            },

            {
              title: "Next Performance Review Date",
              dataIndex: "nextPerformanceReviewDate",
              width: '250px',
              render: (value: string | null) => value ? dayjs(value).format("DD-MMM-YYYY") : "",
            },

            {
              title: "Next Appraisal/Salary Revision Date",
              dataIndex: "nextAppraisalSalaryRevisionDate",
              width: '250px',
              render: (value: string | null) => value ? dayjs(value).format("DD-MMM-YYYY") : "",
            },
            { title: "Department", dataIndex: "departmentName", render: (value: string) => value || "-" },
            { title: "Designation", dataIndex: "designation", render: (value: string) => value || "-" },

            {
              title: "Action", render: (_: unknown, row) => canEdit ? <Button type="link" onClick={() => openStaffProfile(row)}>
                Edit
              </Button> : "-"
            },
          ]}
        />}
    </div>
  </div>;
}
